import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import React from 'react';
import json1 from 'ot-json1';
import produce from 'immer';
import defaultsDeep from 'lodash/defaultsDeep';
import { CollabDocumentContext } from '../components/CollabDocumentProvider';
import DatabaseValue from '../components/Database/models/Value';
import { DATABASE_SCHEMA_VERSION } from '../components/Database/constants';
export function init(data) {
  var operations = [];
  var defaultValue = DatabaseValue.create();
  var value = produce(data, function (state) {
    defaultsDeep(state, defaultValue); // We force update the version number by taking it out of the data

    state.attributes.version = DATABASE_SCHEMA_VERSION;
  }, function (patches) {
    if (!patches || patches.length === 0) return;
    operations.push({
      action: {},
      changes: patches
    });
  });
  return {
    value: value,
    operations: operations
  };
}

function diff(operations) {
  return operations.filter(function (operation) {
    return !['setSelection'].includes(operation.action.type);
  }).reduce(function (memo, _ref) {
    var changes = _ref.changes;
    return memo.concat(changes);
  }, []).map(function (change) {
    switch (change.op) {
      case 'replace':
        return json1.replaceOp(change.path, true, change.value);

      case 'add':
        return json1.insertOp(change.path, change.value);

      case 'remove':
        return json1.removeOp(change.path);

      default:
        return null;
    }
  }).filter(function (x) {
    return x;
  }).reduce(json1.type.compose, null);
}

function apply(ops, databaseValue) {
  return {
    value: json1.type.apply(databaseValue, ops)
  };
}

export default function provideDatabaseValue() {
  var contextType = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : CollabDocumentContext;
  return function (WrappedComponent) {
    return function ComponentWithDatabaseValue(_ref2) {
      var readOnly = _ref2.readOnly,
          userCanEdit = _ref2.userCanEdit,
          _ref2$onChange = _ref2.onChange,
          onChange = _ref2$onChange === void 0 ? function (x) {
        return x;
      } : _ref2$onChange,
          props = _objectWithoutProperties(_ref2, ["readOnly", "userCanEdit", "onChange"]);

      var _useSyncDatabaseValue = useSyncDatabaseValue({
        contextType: contextType,
        readOnly: readOnly,
        userCanEdit: userCanEdit,
        onChange: onChange
      }),
          _useSyncDatabaseValue2 = _slicedToArray(_useSyncDatabaseValue, 5),
          value = _useSyncDatabaseValue2[0],
          selection = _useSyncDatabaseValue2[1],
          onLocalChange = _useSyncDatabaseValue2[2],
          onError = _useSyncDatabaseValue2[3],
          data = _useSyncDatabaseValue2[4];

      return React.createElement(WrappedComponent, Object.assign({}, props, {
        readOnly: readOnly,
        userCanEdit: userCanEdit,
        value: value,
        selection: selection,
        data: data,
        onChange: onLocalChange,
        onError: onError
      }));
    };
  };
}

function useSyncDatabaseValue(_ref3) {
  var readOnly = _ref3.readOnly,
      userCanEdit = _ref3.userCanEdit,
      contextType = _ref3.contextType,
      onChange = _ref3.onChange;

  // @ts-ignore
  var _React$useContext = React.useContext(contextType),
      doc = _React$useContext.doc,
      onError = _React$useContext.onError;

  var _React$useState = React.useState(),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      value = _React$useState2[0],
      setValue = _React$useState2[1];

  var _React$useState3 = React.useState(),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      selection = _React$useState4[0],
      setSelection = _React$useState4[1];

  var _React$useState5 = React.useState(),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      data = _React$useState6[0],
      setData = _React$useState6[1];

  var submitOp = React.useCallback(function (operations) {
    var ops = diff(operations);

    if (ops && ops.length > 0) {
      doc.submitOp(ops);
    }
  }, [doc]);
  var onInit = React.useCallback(function () {
    var _init = init(doc.data),
        initValue = _init.value,
        operations = _init.operations;

    if (!readOnly || userCanEdit) {
      submitOp(operations);
    }

    setValue(initValue);
  }, [doc, submitOp, readOnly, userCanEdit]);
  var onRemoteChange = React.useCallback(function (ops, isLocal) {
    if (isLocal) return;

    if (ops.length > 0) {
      try {
        var _apply = apply(ops, value),
            appliedValue = _apply.value;

        setValue(appliedValue);
      } catch (error) {
        onError(error);
      }
    }
  }, [value, onError]);
  React.useEffect(function () {
    if (doc.data) {
      onInit();
    } else {
      setValue(undefined);
      doc.once('load', onInit);
    }

    doc.on('op', onRemoteChange);
    return function () {
      doc.removeListener('load', onInit);
      doc.removeListener('op', onRemoteChange);
    };
  }, [doc, submitOp, readOnly, userCanEdit, onRemoteChange, onInit]);
  var onLocalChange = React.useCallback(function (_ref4) {
    var value = _ref4.value,
        operations = _ref4.operations,
        selection = _ref4.selection,
        data = _ref4.data;
    submitOp(operations);
    setValue(value);
    setSelection(selection);
    setData(data);
    onChange({
      value: value,
      operations: operations,
      selection: selection
    });
  }, [onChange, submitOp]);
  return [value, selection, onLocalChange, onError, data];
}